import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaMapMarkerAlt, FaUtensils, FaArrowLeft } from 'react-icons/fa';
import { Spinner } from 'react-bootstrap'; // Import Spinner for loading state
import '../styles/SearchResults.css';

const SearchResults = () => {
    const location = useLocation();
    const { query, itemName, latitude, longitude, radius, location: locationText } = location.state || {};

    const [restaurantFiltered, setRestaurantFiltered] = useState([]);
    const [groceryFiltered, setGroceryFiltered] = useState([]);
    const [cloudKitchenFiltered, setCloudKitchenFiltered] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSearchResults = async () => {
            setIsLoading(true);
            setError(null);

            const searchTerm = (itemName || query || '').trim();
            let apiUrl = `/api/search?`;
            const params = new URLSearchParams();

            if (searchTerm) {
                params.append('q', searchTerm);
            }
            if (latitude && longitude && radius) {
                params.append('lat', latitude);
                params.append('lng', longitude);
                params.append('radius', radius);
            }

            if (!searchTerm && (!latitude || !longitude)) {
                setRestaurantFiltered([]);
                setGroceryFiltered([]);
                setCloudKitchenFiltered([]);
                setIsLoading(false);
                return;
            }

            apiUrl += params.toString();

            try {
                const res = await fetch(apiUrl);
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                const data = await res.json();

                // --- MODIFICATION START ---
                // Sort each list alphabetically by name
                const sortedRestaurants = (data.restaurants || []).sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                const sortedGroceries = (data.groceryStores || []).sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                const sortedCloudKitchens = (data.cloudKitchens || []).sort((a, b) =>
                    a.name.localeCompare(b.name)
                );

                setRestaurantFiltered(sortedRestaurants);
                setGroceryFiltered(sortedGroceries);
                setCloudKitchenFiltered(sortedCloudKitchens);
                // --- MODIFICATION END ---

            } catch (err) {
                console.error("Error fetching search results:", err);
                setError("Failed to load search results. Please try again.");
                setRestaurantFiltered([]);
                setGroceryFiltered([]);
                setCloudKitchenFiltered([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSearchResults();
    }, [query, itemName, latitude, longitude, radius]);

    // Prepare sections for sorting
    const sections = [
        {
            key: 'restaurants',
            title: 'Restaurants',
            available: restaurantFiltered.length > 0,
            content: restaurantFiltered.length > 0 ? (
                <div className="search-results-grid">
                    {restaurantFiltered.map(r => (
                        <Link
                            to={`/restaurants/${r.slug || r._id}`}
                            key={r._id}
                            className="search-result-card"
                        >
                            <img
                                src={r.image || 'https://placehold.co/120x120/E0E0E0/333333?text=Img'}
                                alt={r.name}
                                className="search-card-img"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/120x120/E0E0E0/333333?text=Img'; }}
                            />
                            <div className="search-card-info">
                                <h3 className="search-card-name">{r.name}</h3>
                                <div className="search-card-cuisine">
                                    <FaUtensils className="search-card-icon" />
                                    {r.cuisine}
                                </div>
                                <div className="search-card-location">
                                    <FaMapMarkerAlt className="search-card-icon" />
                                    {typeof r.location === 'string'
                                        ? r.location
                                        : [r.location?.street, r.location?.city, r.location?.state].filter(Boolean).join(', ')}
                                </div>
                            </div>
                            <span className="search-card-menu-btn">View Menu</span>
                        </Link>
                    ))}
                </div>
            ) : (
                <p className="search-empty">Not available in Restaurants.</p>
            )
        },
        {
            key: 'cloudKitchens',
            title: 'Cloud Kitchens',
            available: cloudKitchenFiltered.length > 0,
            content: cloudKitchenFiltered.length > 0 ? (
                <div className="search-results-grid">
                    {cloudKitchenFiltered.map(k => (
                        <Link
                            to={`/cloudkitchens/${k.slug || k._id}`}
                            key={k._id}
                            className="search-result-card"
                        >
                            <img
                                src={k.image || 'https://placehold.co/120x120/E0E0E0/333333?text=Img'}
                                alt={k.name}
                                className="search-card-img"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/120x120/E0E0E0/333333?text=Img'; }}
                            />
                            <div className="search-card-info">
                                <h3 className="search-card-name">{k.name}</h3>
                                <div className="search-card-cuisine">
                                    <FaUtensils className="search-card-icon" />
                                    {k.cuisine}
                                </div>
                                <div className="search-card-location">
                                    <FaMapMarkerAlt className="search-card-icon" />
                                    {typeof k.location === 'string'
                                        ? k.location
                                        : [k.location?.street, k.location?.city, k.location?.state].filter(Boolean).join(', ')}
                                </div>
                            </div>
                            <span className="search-card-menu-btn">View Menu</span>
                        </Link>
                    ))}
                </div>
            ) : (
                <p className="search-empty">Not available in Cloud Kitchens.</p>
            )
        },
        {
            key: 'groceryStores',
            title: 'Grocery Stores',
            available: groceryFiltered.length > 0,
            content: groceryFiltered.length > 0 ? (
                <div className="search-results-grid">
                    {groceryFiltered.map(g => (
                        <Link
                            to={`/grocery-stores/${g.slug || g.id || g._id}`}
                            key={g.id || g._id}
                            className="search-result-card"
                        >
                            <img
                                src={g.image || 'https://placehold.co/120x120/E0E0E0/333333?text=Img'}
                                alt={g.name}
                                className="search-card-img"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/120x120/E0E0E0/333333?text=Img'; }}
                            />
                            <div className="search-card-info">
                                <h3 className="search-card-name">{g.name}</h3>
                                <div className="search-card-location">
                                    <FaMapMarkerAlt className="search-card-icon" />
                                    {[g.location?.street, g.location?.city, g.location?.state].filter(Boolean).join(', ')}
                                </div>
                            </div>
                            <span className="search-card-menu-btn">View Store</span>
                        </Link>
                    ))}
                </div>
            ) : (
                <p className="search-empty">Not available in Grocery Stores.</p>
            )
        }
    ];

    // Sort: available sections first, then not available
    const sortedSections = [
        ...sections.filter(s => s.available),
        ...sections.filter(s => !s.available)
    ];

    const displaySearchTerm = itemName || query || 'everything';
    const displayLocation = locationText ? ` near "${locationText}"` : '';

    return (
        <div className="search-bg">
            <div className="search-results-page-container">
                <Link to="/" className="search-back-btn">
                    <FaArrowLeft style={{ marginRight: 8 }} />
                </Link>
                <h2 className="search-title">
                    Search Results for
                    <span className="search-query">"{displaySearchTerm}"</span>
                    {displayLocation}
                </h2>

                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger text-center" role="alert">
                        {error}
                    </div>
                ) : (
                    <>
                        {sortedSections.map((section, idx) => (
                            <React.Fragment key={section.key}>
                                <h3 className="search-section-title" style={section.key !== 'restaurants' ? { marginTop: '2.5rem' } : {}}>
                                    {section.title}
                                </h3>
                                {section.content}
                            </React.Fragment>
                        ))}

                        {/* Show "No results found" only if all are empty */}
                        {restaurantFiltered.length === 0 && groceryFiltered.length === 0 && cloudKitchenFiltered.length === 0 && (
                            <p className="search-empty">No results found for "{displaySearchTerm}"{displayLocation}.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchResults;
